import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import ImportExportBar from "./ImportExportBar";

const emptyForm = { productId: "", warehouseId: "", quantityChange: "", batchNumber: "", reason: "" };

const StockBalances = () => {
  const [balances, setBalances] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productFilter, setProductFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadProducts = async () => {
    try {
      const data = await api.get("/api/v1/products", { size: 200 });
      setProducts(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load products");
    }
  };

  const loadBalances = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/inventory/stock-balance", {
        size: 200,
        productId: productFilter || undefined,
        batchNumber: batchFilter || undefined,
      });
      setBalances(data?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load stock balances");
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const data = await api.get("/api/v1/warehouses/all");
      setWarehouses(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load warehouses");
    }
  };

  useEffect(() => {
    loadProducts();
    loadWarehouses();
  }, []);

  useEffect(() => {
    loadBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFilter, batchFilter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/v1/inventory/adjustments", {
        productId: Number(formData.productId),
        warehouseId: formData.warehouseId ? Number(formData.warehouseId) : undefined,
        quantityChange: Number(formData.quantityChange),
        batchNumber: formData.batchNumber || null,
        reason: formData.reason,
      });
      toast.success("Stock adjustment posted");
      setShowModal(false);
      setFormData(emptyForm);
      loadBalances();
    } catch (error) {
      toast.error(error.message || "Failed to post adjustment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading stock balances...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}>
          <option value="">All products</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.skuCode})
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Filter by batch #"
          value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)}
        />
        <button className="primary" onClick={() => setShowModal(true)}>
          + Stock Adjustment
        </button>
        <ImportExportBar
          importPath="/api/v1/inventory/import"
          exportPath="/api/v1/inventory/export"
          exportFilename="stock-balance.xlsx"
          extraParams={{ productId: productFilter || undefined, batchNumber: batchFilter || undefined }}
          label="Stock"
          onImported={loadBalances}
        />
        <button
          type="button"
          className="secondary"
          onClick={() =>
            api.downloadFile("/api/v1/inventory/import/template", {}, "stock-import-template.xlsx").catch((error) => toast.error(error.message || "Could not download the template"))
          }
        >
          Stock Template
        </button>
      </div>
      <div className="page-subtitle" style={{ margin: "4px 0 10px" }}>
        Excel import: <b>Product Code</b> and <b>Stock Quantity</b> (the stock becomes exactly that, so importing again changes nothing), or
        Quantity Change (added to the stock, with a Reason). Optional: Warehouse Name (blank = default warehouse), Batch Number, Reason.
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Warehouse</th>
              <th>Batch #</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {balances.length === 0 ? (
              <tr>
                <td colSpan={5}>No stock balances found.</td>
              </tr>
            ) : (
              balances.map((balance) => (
                <tr key={balance.id}>
                  <td>{balance.productName}</td>
                  <td>{balance.skuCode}</td>
                  <td>
                    {balance.warehouseName}{" "}
                    <span className={`badge ${balance.warehouseType === "BAD" ? "badge-red" : "badge-green"}`}>
                      {balance.warehouseType === "BAD" ? "Bad" : "Good"}
                    </span>
                  </td>
                  <td>{balance.batchNumber || "-"}</td>
                  <td>{balance.quantity}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box" style={{ maxWidth: "480px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Stock Adjustment</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid" style={{ gridTemplateColumns: "1fr" }}>
                <div className="form-group">
                  <label>Product</label>
                  <select name="productId" value={formData.productId} onChange={handleChange} required>
                    <option value="">Select product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.skuCode})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Warehouse</label>
                  <select name="warehouseId" value={formData.warehouseId} onChange={handleChange}>
                    <option value="">Default warehouse</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name} ({w.type === "BAD" ? "Bad Stock" : "Good Stock"})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantity Change (use negative to reduce stock)</label>
                  <input type="number" step="0.001" name="quantityChange" value={formData.quantityChange} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Batch # (optional)</label>
                  <input name="batchNumber" value={formData.batchNumber} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Reason</label>
                  <input name="reason" value={formData.reason} onChange={handleChange} required />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : "Post Adjustment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StockBalances;
